import { useEffect, useState, useMemo } from 'react'
import { SUBSCRIPTION_FEATURES } from '@/constants'
import { SubscriptionPlanType } from '@/types'

interface FeatureAccess {
  hasAccess: boolean
  isLoading: boolean
  error: string | null
}

interface FeatureLimit {
  limit: number
  isLoading: boolean
  error: string | null
}

interface SubscriptionInfo {
  hasPremium: boolean
  plan: SubscriptionPlanType
  source: string | null
  expiryDate: string | null
  features: string[]
  limits: Record<string, number>
  isLoading: boolean
  error: string | null
}

export const useFeatureAccess = (featureName: string): FeatureAccess => {
  const [state, setState] = useState<FeatureAccess>({
    hasAccess: false,
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await fetch('/api/v1/subscriptions/check-feature', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ feature: featureName }),
        })

        if (!response.ok) {
          throw new Error('Failed to check feature access')
        }

        const data = await response.json()
        setState({
          hasAccess: data.hasAccess,
          isLoading: false,
          error: null,
        })
      } catch (error) {
        console.error(`Error checking access for ${featureName}:`, error)
        setState({
          hasAccess: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    }

    checkAccess()
  }, [featureName])

  return state
}

export const useFeatureLimit = (featureName: string): FeatureLimit => {
  const [state, setState] = useState<FeatureLimit>({
    limit: 0,
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    const checkLimit = async () => {
      try {
        const response = await fetch('/api/v1/subscriptions/get-limit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ feature: featureName }),
        })

        if (!response.ok) {
          throw new Error('Failed to check feature limit')
        }

        const data = await response.json()
        setState({
          limit: data.limit,
          isLoading: false,
          error: null,
        })
      } catch (error) {
        console.error(`Error checking limit for ${featureName}:`, error)
        setState({
          limit: 0,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    }

    checkLimit()
  }, [featureName])

  return state
}

export const useSubscriptionInfo = (): SubscriptionInfo => {
  const [state, setState] = useState<SubscriptionInfo>({
    hasPremium: false,
    plan: SubscriptionPlanType.Free,
    source: null,
    expiryDate: null,
    features: [],
    limits: {},
    isLoading: true,
    error: null,
  })

  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      try {
        const response = await fetch('/api/v1/subscriptions/info')
        if (!response.ok) {
          throw new Error('Failed to fetch subscription info')
        }

        const data = await response.json()
        setState({
          ...data,
          isLoading: false,
          error: null,
        })
      } catch (error) {
        setState({
          hasPremium: false,
          plan: SubscriptionPlanType.Free,
          source: null,
          expiryDate: null,
          features: [],
          limits: {},
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    }

    fetchSubscriptionInfo()
  }, [])

  return state
}

/**
 * Combined hook for subscription functionality
 */
export const useSubscription = () => {
  const subscriptionInfo = useSubscriptionInfo()
  
  // Helper function to check if user has a specific premium feature
  const hasPremiumFeature = useMemo(() => {
    return (featureKey: string): boolean => {
      if (subscriptionInfo.isLoading) return false
      
      // Check if the feature is in the user's features list
      return subscriptionInfo.features.includes(featureKey)
    }
  }, [subscriptionInfo.isLoading, subscriptionInfo.features])
  
  // Helper function to get a feature limit
  const getFeatureLimit = useMemo(() => {
    return (featureKey: string): number => {
      if (subscriptionInfo.isLoading) return 0
      
      // Return the limit value or 0 if not found
      return subscriptionInfo.limits[featureKey] || 0
    }
  }, [subscriptionInfo.isLoading, subscriptionInfo.limits])
  
  return {
    ...subscriptionInfo,
    hasPremiumFeature,
    getFeatureLimit
  }
}